import { DEFAULT_PAGE_INFO, ListingStatus, SortInput } from '@apeiron/common'
import { useFrontendConfigContext } from '@apeiron/config'
import {
  PlanetFilterInput,
  PlanetLite,
  PlanetSortBy,
  PlanetStage
} from '@apeiron/planet'
import {
  deserializeListData,
  deserializePageInfo,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { planetLiteSchema } from '@src/deserialize/yup/planet'
import queryGraphQL from '@src/graphql/query/getMyPlanets'
import { PlanetLiteDeserializeOption } from '@src/types/deserialize'
import { deserializePlanetLite } from '@src/util/apeiron/planet'
import * as R from 'ramda'
import { useCallback, useState } from 'react'

const useGetMyPlanets = (): Hook => {
  const [getPlanets, { loading, called }] = useLazyQuery(queryGraphQL)

  const { config } = useFrontendConfigContext()

  const [data, setData] = useState<PlanetLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (
      request: GetMyPlanetsRequest,
      option: PlanetLiteDeserializeOption = {}
    ): Promise<Response> => {
      const bookmarked = R.propOr(false, 'bookmarked', request) as boolean

      const nftListingStatus = R.propOr(
        undefined,
        'nftListingStatus',
        request
      ) as ListingStatus | undefined

      const planetIds = R.propOr([], 'planetIds', request) as string[]

      const stage = R.propOr(null, 'stage', request)

      const pageInput = R.propOr({}, 'pageInput', request) as PageInput

      const filterInput = R.propOr(
        {},
        'filterInput',
        request
      ) as PlanetFilterInput

      const sortInput = R.propOr(
        {
          sortBy: PlanetSortBy.PriceAsc
        },
        'sortInput',
        request
      ) as SortInput<PlanetSortBy>

      const variables = {
        isBookmarked: bookmarked,
        filterInput: {
          ...filterInput,
          ...(!R.isEmpty(planetIds) ? { planetID: planetIds } : {}),
          ...(!R.isNil(stage) ? { stage } : {}),
          ...(!R.isNil(nftListingStatus) ? { nftListingStatus } : {})
        },
        pagingInput: serializePagingInput(pageInput),
        sortInput
      }

      const response = await getPlanets({ variables })

      const planets = deserializeListData<PlanetLite>(
        response,
        planetLiteSchema,
        config
      )

      const pageInfo = deserializePageInfo(response, pageInput)

      const patchedPlanets = R.map(
        planet => deserializePlanetLite(planet, option),
        planets
      )

      setData(patchedPlanets)
      setPageInfo(pageInfo)

      return {
        data: patchedPlanets,
        pageInfo
      }
    },
    [config, getPlanets]
  )

  const clean = useCallback(() => {
    setData([])
    setPageInfo(DEFAULT_PAGE_INFO)
  }, [])

  return {
    fetch,
    clean,
    data,
    pageInfo,
    loading,
    called
  }
}

export type GetMyPlanetsRequest = {
  bookmarked?: boolean
  nftListingStatus?: ListingStatus
  planetIds?: [string]
  stage?: PlanetStage[]
  pageInput?: PageInput
  filterInput?: PlanetFilterInput
  sortInput?: SortInput<PlanetSortBy>
}

type Response = {
  data: PlanetLite[]
  pageInfo: PageInfo
}

type Hook = {
  fetch: (
    request: GetMyPlanetsRequest,
    option?: PlanetLiteDeserializeOption
  ) => Promise<Response>
  clean: () => void
  data: PlanetLite[]
  pageInfo: PageInfo
  loading: boolean
  called: boolean
}

export default useGetMyPlanets
